import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type Theme = 'light' | 'dark';

const STORAGE_USER_CHOICE = 'famla-theme-user-choice';
/** @deprecated préférer famla-theme-user-choice ; lu une fois pour compatibilité */
const STORAGE_LEGACY = 'famla-theme';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): Theme {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'dark';
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Choix explicite de l'utilisateur, ou null = suivre le système */
function readUserChoiceFromStorage(): Theme | null {
  try {
    const next = localStorage.getItem(STORAGE_USER_CHOICE);
    if (next === 'light' || next === 'dark') {
      return next;
    }
    const legacy = localStorage.getItem(STORAGE_LEGACY);
    if (legacy === 'light' || legacy === 'dark') {
      localStorage.setItem(STORAGE_USER_CHOICE, legacy);
      localStorage.removeItem(STORAGE_LEGACY);
      return legacy;
    }
  } catch (error) {
    console.warn('Error reading theme preference from localStorage:', error);
  }
  return null;
}

function writeUserChoice(theme: Theme) {
  try {
    localStorage.setItem(STORAGE_USER_CHOICE, theme);
    localStorage.removeItem(STORAGE_LEGACY);
  } catch (error) {
    console.warn('Error saving theme preference to localStorage:', error);
  }
}

function subscribePrefersColorScheme(
  callback: (theme: Theme) => void
): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => {};
  }
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const handler = (e: MediaQueryListEvent | MediaQueryList) => {
    const matches = 'matches' in e ? e.matches : mq.matches;
    callback(matches ? 'dark' : 'light');
  };
  handler(mq);
  if (typeof mq.addEventListener === 'function') {
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }
  mq.addListener(handler as (e: MediaQueryListEvent) => void);
  return () => mq.removeListener(handler as (e: MediaQueryListEvent) => void);
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [userChoice, setUserChoice] = useState<Theme | null>(() => readUserChoiceFromStorage());
  const [systemTheme, setSystemTheme] = useState<Theme>(() => getSystemTheme());

  const theme = useMemo(
    () => (userChoice !== null ? userChoice : systemTheme),
    [userChoice, systemTheme]
  );

  const setTheme = (newTheme: Theme) => {
    setUserChoice(newTheme);
    writeUserChoice(newTheme);
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
  };

  useEffect(() => {
    return subscribePrefersColorScheme(setSystemTheme);
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.classList.remove('light-theme', 'dark-theme');
    document.documentElement.classList.add(`${theme}-theme`);
    document.body.style.backgroundColor = theme === 'light' ? '#FFFFFF' : '#0E0F0E';
    document.body.style.color = theme === 'light' ? '#1A1A1A' : '#E8E6E0';
    document.body.style.transition = 'background-color 0.25s ease, color 0.25s ease';
  }, [theme]);

  const value = {
    theme,
    toggleTheme,
    setTheme,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
